// Клієнт для звернень до підсистеми підтримки прийняття рішень (DSS) через
// API ядра gurt-core: усі шляхи проксіюються ядром під /api/dss/..., цей
// модуль напряму до DSS-сервісу не звертається (лише через ядро, як і решта
// сторінок звертається до ${API}/api/...).
//
// Усі шляхи проксіюються ядром під /api/dss/...; фронтенд ходить лише в ядро
// (Gurt_Systems_server/routes/dss.js, routes/allocationPlan.js, routes/import.js),
// яке саме збирає дані зі своєї БД, підписує запит і викликає DSS-модуль.
//
// Ендпоінти DSS-модуля, що проксіюються без зміни шляху й полів (camelCase):
//   - POST /v1/weights, GET /v1/weights/active, POST /v1/weights/{profileId}/activate
//   - GET  /v1/decision/{runId}/explain/{studentId}
// Ендпоінти ядра: /period, /rooms/analyze, /allocation-plan,
// /allocation-plan/apply, /student/:id/decision, /analyze, /counterfactual.

import { getAuthHeader, refreshAccessToken } from "./authToken.js";

const API = process.env.REACT_APP_API_BASE;

export class DssApiError extends Error {
  constructor(status, data) {
    // detail -- помилки DSS-модуля (FastAPI); error -- помилки самого ядра.
    super(
      (data?.detail && String(data.detail)) ||
        (data?.error && String(data.error)) ||
        `Помилка DSS API: ${status}`
    );
    this.status = status;
    this.data = data;
  }
}

async function dssFetch(path, options = {}) {
  // Заголовок читається на кожну спробу: після 401 і refresh повторний
  // запит має піти вже з новим токеном із пам'яті.
  const doFetch = () => {
    const auth = getAuthHeader();
    return fetch(`${API}/api/dss${path}`, {
      ...options,
      headers: {
        ...(auth ? { Authorization: auth } : {}),
        "Content-Type": "application/json",
        ...options.headers,
      },
    });
  };

  let response = await doFetch();
  // Access-токен живе 15 хв (AuthContext оновлює його у фоні кожні 10, але
  // сторінка могла простояти відкритою довше, або фоновий таймер ще не
  // спрацював) -- одна спроба оновити токен за refresh-cookie і повторити
  // запит, перш ніж здаватися.
  if (response.status === 401) {
    const newToken = await refreshAccessToken(API);
    if (newToken) {
      response = await doFetch();
    }
  }

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new DssApiError(response.status, data);
  }
  return data;
}

// -- Підтверджено контрактом DSS-модуля --------------------------------

export function getActiveWeightsProfile(level) {
  return dssFetch(`/v1/weights/active?level=${level}`);
}

export function saveWeightsProfile({ name, level, comparisons, note }) {
  return dssFetch(`/v1/weights`, {
    method: "POST",
    body: JSON.stringify({ name, level, comparisons, note }),
  });
}

export function activateWeightsProfile(profileId) {
  return dssFetch(`/v1/weights/${profileId}/activate`, { method: "POST" });
}

export function getDecisionExplanation(runId, studentId) {
  return dssFetch(`/v1/decision/${runId}/explain/${studentId}`);
}

// -- Маршрути ядра -----------------------------------------------------

// Звітний період за типом прогону: { runType, periodStart, periodEnd }.
export function getReportingPeriodPreview(runType) {
  return dssFetch(`/period?runType=${runType}`);
}

// Аналіз житлового фонду (кімнати беруться з БД ядра): { dormitoryId } необов'язковий.
// Відповідь: { blocks: [{ block, gender: "male"|"female"|"empty"|"unknown",
//   genderConflict, freeBeds, rooms: [{ id, number, capacity, occupied, freeBeds,
//   courseMix, overfilled, unknownResidents }] }], totals: { freeBedsMale,
//   freeBedsFemale, freeBedsEmptyBlocks, freeBedsUnknown, freeBedsTotal,
//   blocksMale, blocksFemale, blocksEmpty, blocksUnknown, roomsWithCourseMix:
//   [roomId] }, skipped: [{ id, reason }] }. unknownResidents -- мешканці без
//   статі чи курсу (такі кімнати не пропускаються; skipped -- лише
//   room_number_invalid).
export function analyzeRooms({ dormitoryId } = {}) {
  return dssFetch(`/rooms/analyze`, {
    method: "POST",
    body: JSON.stringify(dormitoryId ? { dormitoryId: Number(dormitoryId) } : {}),
  });
}

// Картка кімнати: { room: { id, number, block, capacity, accessible,
//   dormitoryId, dormitoryName, freeBeds }, residents: [{ id, pib, group, gender,
//   course, violations }], block: { gender, rooms: [{ id, number, capacity,
//   occupied, freeBeds, courseMix }] }, courseMix, missingData: { gender, course } }.
// 404 -- кімнати немає; 503 (DssApiError.data.degraded) -- DSS недоступний, але
// err.data усе одно містить room і residents.
export function getRoomDetails(id) {
  return dssFetch(`/rooms/${encodeURIComponent(id)}`);
}

// Формує і зберігає план розселення (черга + кімната для кожного).
// Відповідь: { runId, degraded?, summary: { retained, relocated, allocated,
//   unplaced }, plan: [{ id, rank, studentName, status, previousRoomId,
//   previousRoomLabel, roomId, roomLabel, block, blockGender, passNumber,
//   reason, softViolations: ["course_mix"] }], skipped: { applicants:
//   [{ id, reason }], rooms: [{ id, reason }] }, evictionCandidates: [id],
//   effectiveDate }. rank -- місце в черзі (null у деградованому режимі).
export function generateAllocationPlan({
  dormitoryId,
  runType,
  periodStart,
  periodEnd,
  effectiveDate,
  secondPass,
  roomOrder,
}) {
  return dssFetch(`/allocation-plan`, {
    method: "POST",
    body: JSON.stringify({
      dormitoryId: Number(dormitoryId),
      runType,
      periodStart,
      periodEnd,
      effectiveDate,
      secondPass,
      ...(roomOrder ? { roomOrder } : {}),
    }),
  });
}

// Застосовує ЗБЕРЕЖЕНИЙ план за runId (план береться з БД ядра, а не з тіла).
// Відповідь: { runId, applied, total }; повторний виклик -> 409.
export function applyAllocationPlan(runId) {
  return dssFetch(`/allocation-plan/apply`, {
    method: "POST",
    body: JSON.stringify({ runId }),
  });
}

// Пояснення місця здобувача в черзі: { studentId, rank, score, breakdown,
// periodStart, periodEnd, status, currentRoomId, currentRoomLabel, degraded? }.
export function getStudentDecision(studentId) {
  return dssFetch(`/student/${studentId}/decision`);
}

// Аналіз завантаженого рейтингу (сторінка /allocation/import): ядро отримує
// лише числові дані заявників (id, privilege, values.{ses,dorm,act,zone}) і
// повертає чергу з рангом/показником за моделлю та прохідний показник:
//   { runId, normalization, queue: [{ id, rank, score, groupKey, place }],
//     clamped, model, cutoffScore, degraded?: boolean }
// details: true -- відповідь додатково містить model і details кожного елемента
// черги (проміжні величини TOPSIS), clamped -- події обрізання понад шкалу.
export function analyzeAllocation({ places, runType, applicants, normalization, details }) {
  return dssFetch(`/analyze`, {
    method: "POST",
    body: JSON.stringify({
      places,
      runType,
      applicants,
      ...(normalization ? { normalization } : {}),
      ...(typeof details === "boolean" ? { details } : {}),
    }),
  });
}

// "Скільки бракує до прохідного рівня" (формула 16): лише бали s, g, f, q і
// прохідний показник C* -- без ПІБ, id і кімнати. zone іде в запит, бо потрібна
// для C, але приросту за нею немає: здобувач не змінює місце проживання. Визначено лише для
// абсолютного режиму: у класичному DSS відповідає 409.
// Відповідь: { normalization, cutoff, score, dPlus, dMinus, items: [{ code,
// value, min, max, weight, delta, reachable, phi, gainPerPoint }] }.
export function getCounterfactual({ values, cutoff, normalization }) {
  return dssFetch(`/counterfactual`, {
    method: "POST",
    body: JSON.stringify({
      values: { ses: values.ses, dorm: values.dorm, act: values.act, zone: values.zone },
      cutoff,
      ...(normalization ? { normalization } : {}),
    }),
  });
}
